// ============================================
// TTS 큐 관리 모듈 (ES6)
// Google Cloud TTS: 대사 블록만 처리 (나레이션 제외)
// ============================================

import { synthesizeSpeech, cleanTextForTTS } from './api.js';
import {
  getPendingBlock, setPendingBlock, clearPendingBlock,
  getProcessedLength, setProcessedLength,
  getCurrentVoiceId, getIsPlaying, setIsPlaying
} from './state.js';
import { stopAllAudio, getAudioQueue, playAudioFromDataUrl } from './player.js';

// 최소 텍스트 길이
const MIN_TEXT_LENGTH = 5;

// 에러 제한 및 중복 방지
let errorCount = 0;
const MAX_ERRORS = 3;
const processedTexts = new Set();

// 동시 실행 방지 플래그
let isProcessing = false;

// TTS 요청 순차 처리 큐 (순서 보장)
const requestQueue = [];
let isRequesting = false;

// ============================================
// 스트리밍 텍스트 처리 (태그 블록 단위)
// ============================================

/**
 * 스트리밍 중 텍스트 처리
 * - 새 태그가 시작되면 이전 블록 완료 → TTS 요청
 * - 태그가 없는 텍스트는 현재 블록에 누적
 * @param {string} fullText - 현재까지 받은 전체 텍스트
 */
export async function processStreamingText(fullText) {
  // 동시 실행 방지
  if (isProcessing) return;
  isProcessing = true;

  try {
    const characterVoiceId = getCurrentVoiceId();
    if (!characterVoiceId) return;

    // 에러 횟수 초과 시 중단
    if (errorCount >= MAX_ERRORS) {
      console.warn('[TTS] 에러 횟수 초과, 스트리밍 TTS 중단');
      return;
    }

    const processedLength = getProcessedLength();

    // 새 메시지 시작 시 중복 체크 Set 초기화
    if (processedLength === 0) {
      processedTexts.clear();
      errorCount = 0;
    }

    const newText = fullText.slice(processedLength);
    if (!newText) return;

    // 디버그: 실제 텍스트 확인
    console.log('[TTS] newText:', newText.substring(0, 100));

    // 태그 패턴: {C}, {C:happy}, {N}, {N:dramatic}, {T}
    // matchAll 사용으로 regex 상태 문제 해결
    const matches = Array.from(newText.matchAll(/\{([CNT])(?::([a-zA-Z]+))?\}/g));
    console.log('[TTS] 발견된 태그 수:', matches.length, matches.map(m => m[0]));

    let lastIndex = 0;
    // pendingBlock 복사본 사용 (참조 문제 방지)
    let block = { ...getPendingBlock() };

    for (const match of matches) {
      // 이전 블록에 태그 전까지 텍스트 추가
      if (match.index > lastIndex) {
        block.text += newText.slice(lastIndex, match.index);
      }

      console.log('[TTS] 태그 발견:', match[0], '| 이전 블록:', block.type, block.text?.substring(0, 20));

      // 이전 블록이 대사(C) 또는 나레이션(N)이고 텍스트가 있으면 TTS 요청
      if ((block.type === 'C' || block.type === 'N') && block.text.trim()) {
        await sendBlockToTTS(block, characterVoiceId);
      }

      // 새 블록 시작
      block = {
        type: match[1],        // C, N, T
        emotion: match[2] || null,
        text: ''
      };

      lastIndex = match.index + match[0].length;
    }

    // 남은 텍스트는 현재 블록에 추가 (아직 완료 아님)
    if (lastIndex < newText.length) {
      block.text += newText.slice(lastIndex);
    }

    setPendingBlock(block);
    setProcessedLength(fullText.length);
  } finally {
    isProcessing = false;
  }
}

// 나레이션용 기본 음성
const NARRATOR_VOICE = 'zephyr';

/**
 * 블록을 TTS로 전송
 * - 대사({C}): 캐릭터 음성
 * - 나레이션({N}): 기본 여성 음성 (zephyr)
 * - 생각({T}): 제외
 */
async function sendBlockToTTS(block, characterVoiceId) {
  console.log('[TTS] sendBlockToTTS:', block.type, block.text?.substring(0, 30));

  // 생각(T)은 제외
  if (block.type === 'T') {
    console.log('[TTS] 스킵 - 생각:', block.type);
    return;
  }

  // 대사(C)와 나레이션(N)만 처리
  if (block.type !== 'C' && block.type !== 'N') {
    console.log('[TTS] 스킵 - 알 수 없는 타입:', block.type);
    return;
  }

  // 음성 선택: 대사는 캐릭터 음성, 나레이션은 기본 여성 음성
  const voiceId = block.type === 'C' ? characterVoiceId : NARRATOR_VOICE;
  const label = block.type === 'C' ? '대사' : '나레이션';

  // 빈 줄(\n\n)로 분리하여 각각 TTS 요청
  const segments = block.text.split(/\n\s*\n/).filter(s => s.trim());

  for (const segment of segments) {
    const cleanedText = cleanTextForTTS(segment);
    if (cleanedText.length < MIN_TEXT_LENGTH) {
      console.log('[TTS] 스킵 - 텍스트 짧음:', cleanedText.length, '자');
      continue;
    }

    console.log('[TTS] 큐 추가 (' + label + '):', cleanedText.substring(0, 40));
    await addToQueue(cleanedText, voiceId, block.emotion);
  }
}

/**
 * 간단한 해시 함수 (중복 체크용)
 */
function simpleHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 32bit 정수로 변환
  }
  return hash.toString(36);
}

/**
 * TTS 요청을 큐에 추가 (순서 보장)
 */
async function addToQueue(text, voiceId, emotion = null) {
  // 중복 체크 (전체 텍스트 해시 + 음성 조합)
  const key = `${simpleHash(text)}_${voiceId}`;
  if (processedTexts.has(key)) {
    console.log('[TTS Queue] 중복 요청 무시:', text.substring(0, 20));
    return;
  }
  processedTexts.add(key);

  // 에러 횟수 초과 시 중단
  if (errorCount >= MAX_ERRORS) {
    console.warn('[TTS] 에러 횟수 초과, TTS 요청 중단');
    return;
  }

  // 요청 큐에 추가 (순서 보장을 위해 즉시 실행하지 않음)
  requestQueue.push({ text, voiceId, emotion });
  console.log('[TTS Queue] 요청 추가됨 (대기:', requestQueue.length, '):', text.substring(0, 25));

  // 처리 시작
  processRequestQueue();
}

/**
 * 요청 큐 순차 처리 (순서 보장)
 */
async function processRequestQueue() {
  // 이미 처리 중이면 리턴 (순차 보장)
  if (isRequesting) return;
  if (requestQueue.length === 0) return;

  isRequesting = true;

  while (requestQueue.length > 0) {
    const { text, voiceId, emotion } = requestQueue.shift();

    try {
      console.log('[TTS Queue] 요청 처리 중:', text.substring(0, 25));
      const audioUrl = await synthesizeSpeech(text, voiceId, emotion);

      // 오디오 큐에 순서대로 추가
      getAudioQueue().push(audioUrl);
      console.log('[TTS Queue] 오디오 추가 완료');

      // 성공 시 에러 카운트 리셋
      errorCount = 0;

      // 재생 중이 아니면 시작
      if (!getIsPlaying()) {
        playNextInQueue();
      }
    } catch (error) {
      errorCount++;
      console.error(`[TTS Queue] 처리 실패 (${errorCount}/${MAX_ERRORS}):`, error.message);

      if (errorCount >= MAX_ERRORS) {
        console.warn('[TTS] 에러 횟수 초과, 이후 TTS 요청 중단됨');
        break;
      }
    }
  }

  isRequesting = false;
}

// ============================================
// 스트리밍 완료 시 마지막 블록 처리
// ============================================

/**
 * 스트리밍 완료 - 마지막 블록 TTS 요청
 */
export async function flushPendingText() {
  const block = getPendingBlock();
  const characterVoiceId = getCurrentVoiceId();

  console.log('[TTS] flushPendingText:', block.type, block.text?.substring(0, 30));

  if (block.type && block.text.trim() && characterVoiceId) {
    await sendBlockToTTS(block, characterVoiceId);
  }

  clearPendingBlock();
}

// ============================================
// 오디오 큐 재생
// ============================================

/**
 * 큐의 다음 오디오 재생 (Web Audio API)
 */
export async function playNextInQueue() {
  const queue = getAudioQueue();

  if (queue.length === 0) {
    setIsPlaying(false);
    return;
  }

  setIsPlaying(true);
  const audioUrl = queue.shift();

  try {
    // Web Audio API로 재생 (재생 완료까지 await)
    await playAudioFromDataUrl(audioUrl);
    // 재생 완료 → 다음 오디오
    playNextInQueue();
  } catch (error) {
    console.error('[TTS Queue] 재생 실패:', error);
    // 에러 발생해도 다음 오디오 시도
    playNextInQueue();
  }
}

/**
 * 큐 초기화 및 재생 중지
 */
export function clearQueue() {
  stopAllAudio();
  setProcessedLength(0);
  clearPendingBlock();
  // 에러 카운트 및 중복 체크 초기화
  errorCount = 0;
  processedTexts.clear();
  isProcessing = false;
  // 요청 큐 초기화
  requestQueue.length = 0;
  isRequesting = false;
}
